import { Component, inject, computed, signal, ChangeDetectionStrategy, ElementRef, AfterViewInit, OnDestroy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { ToolbarModule } from "primeng/toolbar";
import { PopoverModule } from "primeng/popover";

import { selectUserContext } from "src/app/store/user/user.selectors";
import { selectIsQuoteDetailView } from "src/app/store/navigation/navigation.selectors";
import { selectIsSubscriptionQuote } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { selectLoadingInitializing, selectLoadingFields } from "src/app/store/pages/quote/quote.selectors";
import { selectAsideExpanded } from "src/app/store/navigation/navigation.selectors";
import { selectApplicationVersion } from "src/app/store/application/settings/settings.selectors";
import { selectValidationDisplayConfig } from "src/app/store/pages/quote/validation/validation.selectors";
import { selectSubscriptionTotalMonthlyPriceInclPlants, selectSubscriptionTotalAdditionsExVat } from "src/app/store/pages/quote/version/version.selectors";
import { QuoteToolbarViewComponent } from "./views/quote/quote-toolbar-view.component";
import { QuoteActionsComponent } from "src/app/pages/quote/actions/quote-actions/quote-actions.component";
import { QuoteErrorActionsComponent } from "src/app/pages/quote/actions/quote-error-actions/quote-error-actions.component";
import { CoreCurrencyPipe } from "src/app/shared/pipe/core-currency.pipe";
import { CorePercentagePipe } from "src/app/shared/pipe/core-percentage.pipe";
import { KbdShortcutComponent } from "src/app/shared/components/kbd-shortcut/kbd-shortcut.component";
import { KEYBOARD_SHORTCUTS } from "src/app/shared/constants/keyboard-shortcuts";
import { getShortCurrency } from "src/app/shared/util/currency.utils";
import { NotificationDialogComponent } from "./views/home/components/notification-dialog/notification-dialog.component";
import { LocaleService } from "src/app/shared/util/locale.service";

@Component({
    selector: "app-navigation-header",
    templateUrl: "./navigation-header.component.html",
    styleUrls: ["./navigation-header.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ToolbarModule,
        TranslateModule,
        PopoverModule,
        QuoteToolbarViewComponent,
        QuoteActionsComponent,
        QuoteErrorActionsComponent,
        CoreCurrencyPipe,
        CorePercentagePipe,
        KbdShortcutComponent,
        NotificationDialogComponent,
    ],
})
export class NavigationHeaderComponent implements AfterViewInit, OnDestroy {

    public readonly SHORTCUTS = KEYBOARD_SHORTCUTS;

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);
    private readonly elementRef = inject(ElementRef);
    private readonly locale = inject(LocaleService);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly user = toSignal(this.store.select(selectUserContext), { initialValue: null });
    public readonly applicationVersion = toSignal(this.store.select(selectApplicationVersion), { initialValue: "" });
    private readonly isQuoteDetailViewFromStore = toSignal(this.store.select(selectIsQuoteDetailView), { initialValue: false });
    private readonly isSubscriptionQuoteFromStore = toSignal(this.store.select(selectIsSubscriptionQuote), { initialValue: false });

    // Quote-specific
    public readonly selectedVersion = toSignal(this.store.select(selectVersion));
    public readonly loadingQuote = toSignal(this.store.select(selectLoadingInitializing), { initialValue: true });
    private readonly loadingFields = toSignal(this.store.select(selectLoadingFields), { initialValue: {} });
    private readonly asideExpanded = toSignal(this.store.select(selectAsideExpanded), { initialValue: false });
    private readonly validationConfig = toSignal(this.store.select(selectValidationDisplayConfig));

    // Subscription-specific
    private readonly monthlyPrice = toSignal(this.store.select(selectSubscriptionTotalMonthlyPriceInclPlants), { initialValue: 0 });
    private readonly oneTimeCostTotal = toSignal(this.store.select(selectSubscriptionTotalAdditionsExVat), { initialValue: 0 });

    // =========================================================================
    // COMPUTED - Active View
    // =========================================================================
    public readonly activeView = computed<"quote" | "subscription" | "default">(() => {
        if (!this.isQuoteDetailViewFromStore()) return "default";
        if (this.isSubscriptionQuoteFromStore()) return "subscription";
        return "quote";
    });

    // Quote computed
    public readonly totalSum = computed(() => this.selectedVersion()?.summary?.totalSalesPriceExVat ?? 0);
    public readonly grossMargin3 = computed(() => this.selectedVersion()?.summary?.grossMargin3 ?? 0);
    public readonly isSaving = computed(() => Object.values(this.loadingFields()).some(loading => loading));
    public readonly hasValidationIssues = computed(() => {
        const config = this.validationConfig();
        return (config?.errorCount ?? 0) > 0 || (config?.warningCount ?? 0) > 0;
    });

    // =========================================================================
    // SUMMARY ITEMS (data-driven for responsive overflow)
    // =========================================================================
    public readonly currencyShort = computed(() =>
        getShortCurrency(this.user()?.selectedCompany?.currencyCode ?? "SEK"),
    );

    private readonly subscriptionTotal = computed(() => this.selectedVersion()?.summary?.totalSalesPriceExVat ?? 0);
    private readonly subscriptionTG3 = computed(() => this.selectedVersion()?.summary?.grossMargin3 ?? 0);

    public readonly summaryItems = computed(() => {
        this.locale.currentLang();
        if (this.activeView() === "subscription") {
            return [
                { label: this.translate.instant("SUBSCRIPTION.TOOLBAR.MONTHLY"), suffix: ` ${this.currencyShort()}${this.translate.instant("SUBSCRIPTION.TOOLBAR.PER_MONTH")}`, value: this.monthlyPrice(), type: "currency-suffix" as const },
                { label: this.translate.instant("SUBSCRIPTION.TOOLBAR.ONE_TIME"), value: this.oneTimeCostTotal(), type: "currency" as const },
                { label: "Total", value: this.subscriptionTotal(), type: "currency" as const },
                { label: "TG3", value: this.subscriptionTG3(), type: "percentage" as const },
            ];
        }
        return [
            { label: "Total", value: this.totalSum(), type: "currency" as const },
            { label: "TG3", value: this.grossMargin3(), type: "percentage" as const },
        ];
    });

    public readonly visibleSummaryCount = signal(5);
    private resizeObserver: ResizeObserver | null = null;

    public readonly visibleSummaryItems = computed(() =>
        this.summaryItems().slice(0, this.visibleSummaryCount()),
    );
    public readonly hasOverflow = computed(() =>
        this.summaryItems().length > this.visibleSummaryCount(),
    );

    /** Hide toolbar summary when the aside panel is expanded */
    public readonly showSummary = computed(() =>
        this.activeView() !== "default" && !this.asideExpanded(),
    );

    // =========================================================================
    // LIFECYCLE
    // =========================================================================
    public ngAfterViewInit(): void {
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? 0;
            if (width === 0) return;
            // Non-summary chrome: identity breadcrumb on the left, error-actions
            // + saving spinner in the centre, split-button + cog on the right.
            // The split-button adds ~40px over the plain primary button, hence
            // the bumped baseline from 450 → 520.
            const available = width - 520;
            // Worst-case subscription summary item ("Engångskost.: 200 007,23 kr")
            // measures ~220px including padding + divider. 240 leaves a safety
            // margin so labels never collide on narrower viewports.
            const count = Math.max(0, Math.min(5, Math.floor(available / 240)));
            this.visibleSummaryCount.set(count);
        });
        this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    public ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public toggleSummaryPopover(btn: HTMLElement, popover: any): void {
        popover.toggle({ currentTarget: btn, stopPropagation: () => {}, preventDefault: () => {} }, btn);
    }
}
